import { Injectable, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent, map, startWith } from 'rxjs';

/**
 * Tags de breakpoint. Matchean exactamente los del sistema de grid de
 * NG-ZORRO (`<nz-col nzXs nzSm nzMd nzLg nzXl nzXxl>`) y Ant Design en general.
 * Asi tus media queries en SCSS y los `@if` en plantilla apuntan al MISMO
 * umbral que las columnas responsive de la libreria.
 */
export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

/**
 * BreakpointService: signals reactivos del ancho del viewport.
 *
 * Una sola fuente de verdad: cualquier componente puede inyectarlo y
 * reaccionar al tamaño de la pantalla sin escuchar `resize` por su cuenta.
 *
 * Uso desde un componente:
 * ```ts
 * private bp = inject(BreakpointService);
 *
 * // en template:
 * @if (bp.isMobile()) { <button>Menu</button> }
 *
 * // en TS:
 * toObservable(this.bp.isDesktop)
 *   .pipe(takeUntilDestroyed())
 *   .subscribe((isDesktop) => { ... });
 * ```
 *
 * El resize se debounce a 100ms para no disparar updates en cada pixel
 * mientras el usuario arrastra el borde de la ventana. Como los flags
 * (isMobile / isTablet / isDesktop) son `computed` booleanos, los effects
 * que dependan de ellos se ejecutan SOLO cuando cruzas un breakpoint,
 * no en cada pixel.
 */
@Injectable({ providedIn: 'root' })
export class BreakpointService {
  /** Ancho actual del viewport en pixeles. Se actualiza con debounce de 100ms. */
  readonly width = toSignal(
    fromEvent(window, 'resize').pipe(
      debounceTime(100),
      map(() => window.innerWidth),
      startWith(window.innerWidth),
    ),
    { initialValue: window.innerWidth },
  );

  /** Tag actual del breakpoint (xs..xxl). */
  readonly current = computed<Breakpoint>(() => {
    const w = this.width();
    if (w >= BREAKPOINTS.xxl) return 'xxl';
    if (w >= BREAKPOINTS.xl) return 'xl';
    if (w >= BREAKPOINTS.lg) return 'lg';
    if (w >= BREAKPOINTS.md) return 'md';
    if (w >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  });

  /** xs + sm (< 768px): telefonos. */
  readonly isMobile = computed(() => this.width() < BREAKPOINTS.md);

  /** md (768-991px): tablets portrait. */
  readonly isTablet = computed(() => {
    const w = this.width();
    return w >= BREAKPOINTS.md && w < BREAKPOINTS.lg;
  });

  /** lg+ (>= 992px): laptops y monitores. */
  readonly isDesktop = computed(() => this.width() >= BREAKPOINTS.lg);
}
